/* Albatross x Hemisphere — Slideshow (scroll-snap based, vanilla) */
(() => {
  if (customElements.get('hm-slideshow')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HmSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._bound) { this.startAutoplay(); return; }
      this._bound = true;
      this.track = this.querySelector('.hm-ss__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll(':scope > .hm-ss__slide'));
      this.dots = Array.from(this.querySelectorAll('.hm-ss__dot'));
      this.currentEl = this.querySelector('.hm-ss__current');
      this.autoplayBtn = this.querySelector('.hm-ss__autoplay');
      this.current = 0;
      this.paused = false;

      const prev = this.querySelector('.hm-ss__btn--prev');
      const next = this.querySelector('.hm-ss__btn--next');
      prev && prev.addEventListener('click', () => this.go(this.current - 1));
      next && next.addEventListener('click', () => this.go(this.current + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      let scrollTimer;
      this.track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => this.syncFromScroll(), 60);
      }, { passive: true });

      if (this.autoplayBtn) {
        this.autoplayBtn.addEventListener('click', () => {
          this.paused = !this.paused;
          this.classList.toggle('is-paused', this.paused);
          this.autoplayBtn.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
          if (this.paused) this.stopAutoplay();
          else this.startAutoplay();
        });
      }
      this.addEventListener('mouseenter', () => { this.hovered = true; });
      this.addEventListener('mouseleave', () => { this.hovered = false; });
      this.addEventListener('focusin', () => { this.focused = true; });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.focused = false; });

      // Theme editor: show the selected slide and hold autoplay while it is selected
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s === e.target || s.contains(e.target));
        if (i < 0) return;
        this.editorHold = true;
        this.go(i, false);
      });
      this.addEventListener('shopify:block:deselect', () => { this.editorHold = false; });

      this.updateState();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
    }

    syncFromScroll() {
      const width = this.track.clientWidth;
      if (!width) return;
      const i = Math.round(this.track.scrollLeft / width);
      const clamped = Math.max(0, Math.min(this.slides.length - 1, i));
      if (clamped !== this.current) {
        this.current = clamped;
        this.updateState();
      }
    }

    go(index, smooth = true) {
      const count = this.slides.length;
      if (count < 2) return;
      const target = ((index % count) + count) % count;
      this.current = target;
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto'
      });
      this.updateState();
    }

    updateState() {
      this.slides.forEach((slide, i) => {
        const active = i === this.current;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
        if (!active && window.HM && window.HM.pauseVideosIn) window.HM.pauseVideosIn(slide);
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.currentEl) this.currentEl.textContent = this.current + 1;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.paused || !this.slides || this.slides.length < 2) return;
      this.watchVisibility();
      if (document.hidden || this.offscreen) return; // resumes from the observers below
      const seconds = parseFloat(this.dataset.speed) || 5;
      this.timer = window.setInterval(() => {
        if (this.hovered || this.focused || this.editorHold || this.offscreen || document.hidden) return;
        this.go(this.current + 1);
      }, seconds * 1000);
    }

    /* Pause autoplay while the section is off-screen or the tab is hidden; resume when it is back. */
    watchVisibility() {
      if (!this.io && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          const visible = entries[entries.length - 1].isIntersecting;
          this.offscreen = !visible;
          this.syncAutoplay();
        }, { threshold: 0.1 });
        this.io.observe(this);
      }
      if (!this.onVis) this.onVis = () => this.syncAutoplay();
      document.removeEventListener('visibilitychange', this.onVis); // idempotent re-bind
      document.addEventListener('visibilitychange', this.onVis);
    }

    syncAutoplay() {
      const shouldRun = !document.hidden && !this.offscreen;
      if (!shouldRun) { this.stopAutoplay(); return; }
      if (!this.timer && this.dataset.autoplay === 'true' && !this.paused) this.startAutoplay();
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('hm-slideshow', HmSlideshow);
})();
